import { debounce, requestIdleCallback, viewTransition, mediaQueryLarge } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  /** @returns {HTMLDetailsElement | null} */
  #getDetails() {
    const d = this.closest('details');
    return d instanceof HTMLDetailsElement ? d : null;
  }

  /** Mobile collection filter row: escape ancestor overflow/clip by using fixed + summary coordinates. */
  #isMobileHorizontalFacetPopover() {
    return (
      !mediaQueryLarge.matches &&
      this.classList.contains('facets__panel-content') &&
      Boolean(this.closest('.facets--horizontal'))
    );
  }

  #clearMobileFacetFixedLayout() {
    this.style.removeProperty('position');
    this.style.removeProperty('top');
    this.style.removeProperty('left');
    this.style.removeProperty('right');
    this.style.removeProperty('width');
    this.style.removeProperty('max-width');
    this.style.removeProperty('max-height');
    this.style.removeProperty('z-index');
  }

  #applyMobileFacetFixedLayout() {
    const details = this.#getDetails();
    const summary = details?.querySelector('summary');
    if (!(summary instanceof HTMLElement)) return;

    const sr = summary.getBoundingClientRect();
    const margin = 8;
    const maxPanelPx = Math.min(window.innerWidth - margin * 2, 352);
    const left = Math.min(Math.max(margin, sr.left), window.innerWidth - margin - maxPanelPx);
    const top = sr.bottom + 4;
    const maxHeight = Math.max(120, window.innerHeight - top - margin);

    this.style.position = 'fixed';
    this.style.top = `${Math.round(top)}px`;
    this.style.left = `${Math.round(left)}px`;
    this.style.right = 'auto';
    this.style.width = `${Math.round(maxPanelPx)}px`;
    this.style.maxWidth = `${Math.round(maxPanelPx)}px`;
    this.style.maxHeight = `${Math.round(maxHeight)}px`;
    this.style.zIndex = 'var(--layer-overlay)';
  }

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    if (this.#isMobileHorizontalFacetPopover()) {
      const details = this.#getDetails();
      if (details?.open) {
        this.#applyMobileFacetFixedLayout();
      } else {
        this.#clearMobileFacetFixedLayout();
      }
      this.#mutationObserver.takeRecords();
      return;
    }

    this.#clearMobileFacetFixedLayout();

    /* Below 750px, non-facet panels: do not apply desktop offset top/left. */
    if (!mediaQueryLarge.matches) {
      this.style.removeProperty('top');
      this.style.removeProperty('left');
      this.#mutationObserver.takeRecords();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    /* Horizontal facet popovers use CSS `top: calc(100% + …)` on the host; inline top would override it. */
    const isHorizontalFacetPanel =
      this.classList.contains('facets__panel-content') && Boolean(this.closest('.facets--horizontal'));

    if (isHorizontalFacetPanel) {
      this.style.removeProperty('top');
    } else {
      this.style.top = OFFSET + 'px';
    }

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  #scrollReposition = debounce(() => {
    if (this.#isMobileHorizontalFacetPopover() && this.#getDetails()?.open) {
      this.#applyMobileFacetFixedLayout();
    }
  }, 16);

  #onDetailsToggle = () => {
    requestIdleCallback(() => this.#updatePosition());
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);
    window.addEventListener('scroll', this.#scrollReposition, { capture: true, passive: true });

    const details = this.#getDetails();
    details?.addEventListener('toggle', this.#onDetailsToggle);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('scroll', this.#scrollReposition, { capture: true });
    this.#getDetails()?.removeEventListener('toggle', this.#onDetailsToggle);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
